import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { siteImages, products, categories } from '../data/mock';
import { Sun, Coffee, Dumbbell, Moon, Clock, AlertCircle, ArrowRight, CheckCircle } from 'lucide-react';

const HowToUsePage = () => {
  useEffect(() => {
    document.title = 'How to Use | Keto Vybe';
  }, []);

  const routines = [
    {
      icon: Coffee,
      time: 'Morning',
      title: 'Start Your Day Right',
      products: ['BHB Ketone Capsules', 'Keto Electrolyte Complex', 'Complete Keto Multivitamin'],
      tips: [
        'Take BHB ketones with breakfast or on an empty stomach',
        'Electrolytes help with hydration and energy',
        'Multivitamins fill nutritional gaps in your diet'
      ],
      color: 'vybe'
    },
    {
      icon: Dumbbell,
      time: 'Pre-Workout',
      title: 'Power Your Performance',
      products: ['Keto Energy Boost Capsules', 'MCT Oil Softgels', 'Thermogenic Fat Burner'],
      tips: [
        'Take 30-45 minutes before exercise',
        'MCT oil provides quick fuel for workouts',
        'Fat burners can enhance calorie burn during activity'
      ],
      color: 'cyan'
    },
    {
      icon: Sun,
      time: 'Afternoon',
      title: 'Midday Boost',
      products: ['Brain Fuel Nootropic Blend', 'Keto Energy Boost Capsules'],
      tips: [
        'Combat the afternoon slump naturally',
        'Nootropics support focus and productivity',
        'Avoid taking energy products too late in the day'
      ],
      color: 'coral'
    },
    {
      icon: Moon,
      time: 'Evening',
      title: 'Rest & Recover',
      products: ['Keto Sleep Support Formula', 'Keto Probiotics 40 Billion CFU', 'Digestive Enzyme Complex'],
      tips: [
        'Take sleep support 30 minutes before bed',
        'Probiotics can be taken with dinner',
        'Digestive enzymes help process your evening meal'
      ],
      color: 'vybe'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.lifestyleSection}
            alt="How to Use"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Guide</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                How to Use
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Optimize your supplement routine for best results
              </p>
            </div>
          </div>
        </section>

        {/* Daily Routine */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-cyan-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Daily Routine</span>
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
                When to Take Your Supplements
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Timing matters. Here's how to incorporate supplements into your day.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {routines.map((routine, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50">
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`w-14 h-14 bg-${routine.color}-600/20 rounded-xl flex items-center justify-center`}>
                      <routine.icon className={`w-7 h-7 text-${routine.color}-400`} />
                    </div>
                    <div>
                      <span className={`text-${routine.color}-400 text-sm font-medium`}>{routine.time}</span>
                      <h3 className="font-bold text-white text-lg">{routine.title}</h3>
                    </div>
                  </div>
                  
                  <div className="mb-5">
                    <h4 className="text-slate-300 text-sm font-medium mb-2">Recommended Products:</h4>
                    <div className="flex flex-wrap gap-2">
                      {routine.products.map((product, j) => (
                        <span key={j} className="px-3 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full">
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {routine.tips.map((tip, j) => (
                      <li key={j} className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-vybe-400 flex-shrink-0 mt-0.5" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* General Tips */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl text-white mb-4">General Tips for Best Results</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Stay Consistent', desc: 'Take your supplements at the same time each day for best results.' },
                { title: 'Stay Hydrated', desc: 'Drink plenty of water, especially when taking capsules or electrolytes.' },
                { title: 'Follow Dosage', desc: 'Don\'t exceed recommended dosages. More isn\'t always better.' },
                { title: 'Be Patient', desc: 'Give supplements 2-4 weeks to see full effects.' },
                { title: 'Listen to Your Body', desc: 'Adjust timing or dosage if you experience any discomfort.' },
                { title: 'Pair with Diet', desc: 'Supplements work best alongside a proper ketogenic diet.' }
              ].map((tip, i) => (
                <div key={i} className="flex gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-8 h-8 bg-vybe-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-vybe-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-1">{tip.title}</h4>
                    <p className="text-slate-400 text-sm">{tip.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Important Note */}
        <section className="py-12 bg-slate-900">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-800/50 border border-vybe-500/30 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-vybe-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-white mb-2">Important Note</h4>
                  <p className="text-slate-400 text-sm">
                    These supplements are not intended to diagnose, treat, cure, or prevent any disease. 
                    Consult with a healthcare professional before starting any new supplement regimen, 
                    especially if you have existing health conditions or are taking medications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-white mb-4">Ready to Start?</h2>
            <p className="text-slate-400 mb-8">Build your perfect supplement stack today.</p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full hover:shadow-glow-purple transition-all"
            >
              Shop Products
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HowToUsePage;
